import React, { memo } from 'react';

import { Box, Typography } from '@mui/material';
import { AutocompleteCDS, TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import ConditionTasksTable from 'app/pages/underwriting/conditions/ConditionTasksTable';
import PropTypes from 'prop-types';

import { ConditionsTestIds } from '../../Conditions.testIds';

import { ADHOC_CONDITION_MESSAGE_MAX_LENGTH } from './AdhocConditionFields.constants';
import { taskSectionStyles } from './AdhocConditionFields.styles';

const AdhocConditionFields = ({
  form,
  formName,
  validationErrors,
  lockedFieldsMap,
  hiddenFieldsMap,
  priorToOptions,
  slaGroupsOptions,
  taskTypeOptions,
  handleChange,
  onAutocompleteChange,
  setInForm,
  validateField,
  onCreateNewForm,
  isInternal = false,
  isTasksEnabled = false,
  isDisabledByLock = false,
}) => (
  <>
    <TextFieldCDS
      fullWidth
      multiline
      required
      dataTestId={ConditionsTestIds.ADHOC_MESSAGE_INPUT}
      disabled={isDisabledByLock || lockedFieldsMap.internalText}
      error={!!validationErrors.internalText}
      helperText={validationErrors.internalText}
      isHidden={hiddenFieldsMap.internalText}
      label="Message"
      maxLength={ADHOC_CONDITION_MESSAGE_MAX_LENGTH}
      name="internalText"
      rows={4}
      value={form.internalText}
      onChange={handleChange}
    />

    <TextFieldCDS
      fullWidth
      multiline
      dataTestId={ConditionsTestIds.ADHOC_BORROWER_MESSAGE_INPUT}
      disabled={isDisabledByLock || lockedFieldsMap.borrowerInternalText}
      error={!!validationErrors.borrowerInternalText}
      helperText={validationErrors.borrowerInternalText}
      isHidden={hiddenFieldsMap.borrowerInternalText}
      label="Borrower Message"
      maxLength={ADHOC_CONDITION_MESSAGE_MAX_LENGTH}
      name="borrowerInternalText"
      rows={3}
      value={form.borrowerInternalText}
      onChange={handleChange}
    />

    <AutocompleteCDS
      fullWidth
      required
      dataTestId={ConditionsTestIds.ADHOC_PRIOR_TO_SELECT}
      disabled={isDisabledByLock || lockedFieldsMap.priorToType}
      error={!!validationErrors.priorToType}
      helperText={validationErrors.priorToType}
      isHidden={hiddenFieldsMap.priorToType}
      label="Prior To"
      name="priorToType"
      options={priorToOptions}
      value={form.priorToType}
      withSortedOptions={false}
      onChange={onAutocompleteChange}
    />

    {isInternal && (
      <AutocompleteCDS
        fullWidth
        required
        dataTestId={ConditionsTestIds.ADHOC_SLA_GROUP_SELECT}
        disabled={isDisabledByLock || lockedFieldsMap.slaGroupId}
        error={!!validationErrors.slaGroupId}
        helperText={validationErrors.slaGroupId}
        label="SLA Group"
        name="slaGroupId"
        options={slaGroupsOptions}
        value={form.slaGroupId}
        onChange={onAutocompleteChange}
      />
    )}

    {isTasksEnabled && (
      <Box sx={taskSectionStyles}>
        <Typography variant="medium14">Tasks</Typography>
        <ConditionTasksTable
          isBrokerType
          form={form}
          formName={formName}
          handleChange={handleChange}
          isTaskAssignmentEnabled={isTasksEnabled}
          setInForm={setInForm}
          taskTypeOptions={taskTypeOptions}
          tasks={form.taskTypeIds}
          validateField={validateField}
          validationErrors={validationErrors}
          onCreateNewForm={onCreateNewForm}
        />
      </Box>
    )}
  </>
);

AdhocConditionFields.propTypes = {
  form: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  validationErrors: PropTypes.object.isRequired,
  lockedFieldsMap: PropTypes.object.isRequired,
  hiddenFieldsMap: PropTypes.object.isRequired,
  priorToOptions: PropTypes.array.isRequired,
  slaGroupsOptions: PropTypes.array.isRequired,
  taskTypeOptions: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  onAutocompleteChange: PropTypes.func.isRequired,
  onCreateNewForm: PropTypes.func.isRequired,
  isInternal: PropTypes.bool,
  isTasksEnabled: PropTypes.bool,
  isDisabledByLock: PropTypes.bool,
};

export default memo(AdhocConditionFields);
